import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import api from "../lib/api";
import { SessionExpiryDialog } from "../components/SessionExpiryDialog";

const AuthContext = createContext(null);
const FALLBACK_IDLE_MIN = 30;
const WARN_BEFORE_MS = 60 * 1000;
const HEARTBEAT_MS = 5 * 60 * 1000;
const ACTIVITY_KEY = "fluxocrm_last_activity";
export const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "click", "scroll", "touchstart", "pointerdown"];

const readSharedActivity = () => {
  try { return Number(localStorage.getItem(ACTIVITY_KEY)) || 0; } catch { return 0; }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const lastActivityRef = useRef(Date.now());
  const lastSharedWriteRef = useRef(0);
  const lastPingRef = useRef(Date.now());
  const warningRef = useRef(false);
  const expiringRef = useRef(false);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Erro ao encerrar sessão:", error);
    }
    try {
      const keep = ["fluxocrm_device_id", "fluxocrm_pending_orders"];
      Object.keys(localStorage)
        .filter((k) => k.startsWith("fluxocrm_") && !keep.includes(k))
        .forEach((k) => localStorage.removeItem(k));
    } catch (e) {
      console.warn("Não foi possível limpar os caches locais:", e);
    }
    setUser(false);
  }, []);

  // Zera o contador (local + compartilhado entre abas) — mousemove não dispensa o aviso de expiração
  const bump = useCallback((ev) => {
    if (warningRef.current && ev?.type === "mousemove") return;
    const now = Date.now();
    lastActivityRef.current = now;
    if (now - lastSharedWriteRef.current > 2000) {
      lastSharedWriteRef.current = now;
      try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch { /* storage indisponível */ }
    }
  }, []);

  const ping = useCallback(() => {
    lastPingRef.current = Date.now();
    api.post("/auth/ping").then((r) => {
      if (r.data?.idle_timeout_minutes) setUser((u) => (u ? { ...u, idle_timeout_minutes: r.data.idle_timeout_minutes } : u));
    }).catch(() => {});
  }, []);

  const stayLoggedIn = useCallback(() => {
    bump({ type: "click" });
    ping();
  }, [bump, ping]);

  useEffect(() => {
    if (!user) return;
    const idleLimitMs = (Number(user.idle_timeout_minutes) || FALLBACK_IDLE_MIN) * 60 * 1000;
    lastActivityRef.current = Date.now();
    lastPingRef.current = Date.now();
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
    const tick = () => {
      if (expiringRef.current) return;
      const now = Date.now();
      const last = Math.max(lastActivityRef.current, readSharedActivity());
      const left = idleLimitMs - (now - last);
      if (left <= 0) {
        expiringRef.current = true;
        logout().then(() => { window.location.href = "/login?motivo=inatividade"; });
        return;
      }
      if (left <= WARN_BEFORE_MS) {
        warningRef.current = true;
        setRemaining(Math.ceil(left / 1000));
      } else if (warningRef.current) {
        warningRef.current = false;
        setRemaining(null);
      }
      // heartbeat: houve atividade local desde o último contato com o servidor → mantém last_activity no backend
      if (now - lastPingRef.current > HEARTBEAT_MS && last > lastPingRef.current) ping();
    };
    const timer = setInterval(tick, 1000);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, bump));
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
    };
  }, [user, logout, bump, ping]);

  const value = useMemo(() => ({ user, setUser, logout, bumpActivity: bump }), [user, logout, bump]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      {user && remaining !== null && (
        <SessionExpiryDialog seconds={remaining} onStay={stayLoggedIn}
          onLogout={() => { expiringRef.current = true; logout().then(() => { window.location.href = "/login"; }); }} />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
